import type {
  Client,
  EvaluationContext,
  EvaluationDetails,
  JsonValue,
  ResolutionDetails,
} from '@openfeature/web-sdk'
import {
  coerceBoolean,
  coerceNumber,
  coerceObject,
  coerceString,
} from './queryCoercion'

/**
 * What the server evaluated for one request. Travels to the browser in the Nuxt
 * payload so both sides render from the same values.
 */
export interface ServerFlagState {
  /** The evaluation context the server built from the request. */
  context: EvaluationContext
  /** Every flag ConfigCat knows, evaluated for that context. */
  values: Record<string, ServerFlagValue>
  /** True when the ConfigCat client was not available and only defaults exist. */
  degraded: boolean
}

export interface ServerFlagValue {
  value: JsonValue
  reason: 'TARGETING_MATCH' | 'DEFAULT' | 'ERROR' | 'STATIC'
  variant?: string
  /**
   * A query string override. It carries the raw parsed URL value rather than a
   * typed one, because the browser's QueryProvider coerces by the type the
   * caller asks for, and the server has to land on the same answer.
   */
  fromQuery?: boolean
}

type Kind = 'string' | 'boolean' | 'number' | 'object'
type ValueOf<K extends Kind> = K extends 'string'
  ? string
  : K extends 'boolean'
    ? boolean
    : K extends 'number'
      ? number
      : JsonValue

function isKind<K extends Kind>(
  kind: K,
  value: JsonValue
): value is ValueOf<K> {
  // typeof null is 'object', and a flag ConfigCat could not evaluate arrives
  // here as null, so null has to fail the check or the caller gets null back
  // instead of the default it supplied.
  if (kind === 'object') return typeof value === 'object' && value !== null
  return typeof value === kind
}

function resolve<K extends Kind>(
  state: ServerFlagState,
  flagKey: string,
  defaultValue: ValueOf<K>,
  kind: K,
  coerce: (
    value: JsonValue | null,
    def: ValueOf<K>
  ) => ResolutionDetails<ValueOf<K>>
): EvaluationDetails<ValueOf<K>> {
  const entry = state.values[flagKey]
  const details = (
    value: ValueOf<K>,
    reason: ServerFlagValue['reason'],
    variant?: string
  ): EvaluationDetails<ValueOf<K>> => ({
    flagKey,
    value,
    reason,
    variant,
    flagMetadata: {},
  })
  if (!entry) return details(defaultValue, state.degraded ? 'ERROR' : 'DEFAULT')
  if (entry.fromQuery) {
    // Same coercion the browser's QueryProvider applies, driven by the type the
    // caller asked for rather than the type the URL parsed as.
    const coerced = coerce(entry.value, defaultValue)
    return details(
      coerced.value,
      coerced.reason === 'TARGETING_MATCH' ? 'TARGETING_MATCH' : 'DEFAULT'
    )
  }
  // A flag typed differently from what the caller asked for is what the
  // OpenFeature SDKs treat as a type mismatch: the default wins.
  if (!isKind(kind, entry.value)) return details(defaultValue, 'ERROR')
  return details(entry.value, entry.reason, entry.variant)
}

/**
 * A synchronous OpenFeature shaped client backed by the server's evaluation.
 * Covers the methods the app calls (values, details, track). The rest of the
 * web SDK surface is not used during a render.
 */
export function createServerFlagClient(state: ServerFlagState): Client {
  const boolean = (key: string, def: boolean) =>
    resolve(state, key, def, 'boolean', coerceBoolean)
  const string = (key: string, def: string) =>
    resolve(state, key, def, 'string', coerceString)
  const number = (key: string, def: number) =>
    resolve(state, key, def, 'number', coerceNumber)
  const object = (key: string, def: JsonValue) =>
    resolve(state, key, def, 'object', coerceObject)

  const client = {
    metadata: {
      name: 'ssr-flags',
      providerMetadata: { name: 'configcat-server' },
    },
    providerStatus: 'READY',
    getBooleanValue: (key: string, def: boolean) => boolean(key, def).value,
    getStringValue: (key: string, def: string) => string(key, def).value,
    getNumberValue: (key: string, def: number) => number(key, def).value,
    getObjectValue: (key: string, def: JsonValue) => object(key, def).value,
    getBooleanDetails: boolean,
    getStringDetails: string,
    getNumberDetails: number,
    getObjectDetails: object,
    getContext: () => state.context,
    track: () => {},
    addHooks: () => {},
    clearHooks: () => {},
    getHooks: () => [],
    setLogger: () => client,
    clearLogger: () => client,
    addHandler: () => {},
    removeHandler: () => {},
    getHandlers: () => [],
  }
  // The web SDK's Client interface also carries hooks, logger and event handler
  // plumbing the render never touches. Same precedent as sendEvent.ts.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return client as unknown as Client
}
